import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

export interface HeroImage {
  id: string;
  imageUrl: string;
  title: string;
  description: string;
  order: number;
  active: boolean;
}

const COLLECTION_NAME = 'heroImages';

export const heroService = {
  async getAllHeroImages(): Promise<HeroImage[]> {
    try {
      const q = query(collection(db, COLLECTION_NAME));
      const querySnapshot = await getDocs(q);
      
      // Log raw data with full object expansion
      const rawData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Document data:', {
          id: doc.id,
          ...data,
          _rawData: JSON.stringify(data) // This will show us exactly what's in Firestore
        });
        return {
          id: doc.id,
          ...data
        };
      });
      
      return rawData as HeroImage[];
    } catch (error) {
      console.error('Error getting hero images:', error);
      throw error;
    }
  },

  async getActiveHeroImages(): Promise<HeroImage[]> {
    try {
      // First get all images to check the data structure
      const allImages = await this.getAllHeroImages();
      console.log('All images with full data:', allImages.map(img => ({...img, _debug: true})));
      
      // Then query for active ones
      const q = query(
        collection(db, COLLECTION_NAME),
        where('active', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const images = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as HeroImage));
      
      console.log('Active images query result:', images);
      
      // Sort by order after fetching
      return images.sort((a, b) => a.order - b.order);
    } catch (error) {
      console.error('Error getting active hero images:', error);
      throw error;
    }
  },

  async addHeroImage(
    file: File,
    title: string,
    description: string,
    order: number,
    active: boolean = true
  ): Promise<HeroImage> {
    try {
      // Upload image to Firebase Storage
      const storageRef = ref(storage, `hero/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);

      // Add document to Firestore
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        imageUrl,
        title,
        description,
        order: order.toString(),
        active
      });

      return {
        id: docRef.id,
        imageUrl,
        title,
        description,
        order,
        active
      };
    } catch (error) {
      console.error('Error adding hero image:', error);
      throw error;
    }
  },

  async updateHeroImage(
    id: string,
    updates: Partial<Omit<HeroImage, 'id'>>
  ): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      // Convert order to string if it's being updated
      const processedUpdates = {
        ...updates,
        order: updates.order !== undefined ? updates.order.toString() : undefined
      };
      await updateDoc(docRef, processedUpdates);
    } catch (error) {
      console.error('Error updating hero image:', error);
      throw error;
    }
  },

  async deleteHeroImage(id: string, imageUrl: string): Promise<void> {
    try {
      // Delete image from Storage
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);

      // Delete document from Firestore
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting hero image:', error);
      throw error;
    }
  },

  async updateHeroImageWithFile(id: string, file: File, updates: Partial<Omit<HeroImage, 'id' | 'imageUrl'>>): Promise<HeroImage> {
    try {
      // First upload the new image
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'hero');
      
      const uploadResponse = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload new image');
      }
      
      const { url: newImageUrl } = await uploadResponse.json();
      
      // Then update the hero image with new data and URL
      const response = await fetch(`/api/hero/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updates,
          imageUrl: newImageUrl,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update hero image');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating hero image with new file:', error);
      throw error;
    }
  },
}; 